// Presentational parts of the Recording Booth (split out of
// RecordingStudio.tsx): reference-video panel, device / volume controls,
// middle column (voice picker, params, Generate), takes column and footer.
// No state of their own — everything comes from RecordingStudio.

import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { STSParams, Voice } from '@shared/types';
import { RECORDING_SESSION_TAKE_LIMIT } from '@shared/constants';
import type { AudioInputDevice } from '@/hooks/useRecording';
import { formatTakeLabel } from '@/lib/stsTakeLabel';
import { voiceColorVar, voiceLabel } from '@/lib/voiceMapping';
import { Button } from '@/components/ui/Button';
import { VoicePicker } from '@/components/VoicePicker/VoicePicker';
import { RecordingStudioParams } from './params';
import { TakeCard } from './TakeCard';
import type { DraftTake, PermState, RecState } from './boothTypes';
import styles from './RecordingStudio.module.css';

// ── Left column: reference video + countdown / status banners ─────────────
export function BoothVideoPanel({
  videoSrc,
  videoRef,
  videoTime,
  videoDuration,
  countdown,
  stateKind,
}: {
  videoSrc: string | null;
  videoRef: RefObject<HTMLVideoElement | null>;
  videoTime: number;
  videoDuration: number;
  countdown: number | null;
  stateKind: RecState['kind'];
}) {
  return (
    <div className={styles.videoWrap}>
      {videoSrc ? (
        <>
          <video
            ref={videoRef}
            className={styles.video}
            src={videoSrc}
            playsInline
            /* preload="auto" buffers the full video on overlay open
               so: (a) the first frame paints immediately (no black
               canvas until first interaction), (b) play() at the
               end of the countdown starts instantly instead of
               waiting for the network round-trip to fetch data
               chunks, (c) loadedmetadata fires reliably so the
               time-overlay knows the total duration. */
            preload="auto"
          />
          {/* Top-right time overlay — discreet "current / total" in
              whole seconds. 50% gray bg per spec. Always rendered
              once we have a video src so the user sees "0 / 0"
              briefly during load instead of nothing. */}
          <div className={styles.videoTimeOverlay}>
            {Math.floor(videoTime)}s / {Math.floor(videoDuration)}s
          </div>
        </>
      ) : (
        <div className={styles.videoPlaceholder}>
          <i className="ph-bold ph-film-strip" />
          <div>No video reference for this asset</div>
        </div>
      )}

      {countdown != null && (
        <div className={styles.countdown}>
          <div className={styles.countdownNumber}>{countdown}</div>
          <div className={styles.countdownLabel}>Recording in…</div>
        </div>
      )}
      {stateKind === 'recording' && (
        <div className={styles.recordingBanner}>
          <span className={styles.recordingDot} />
          Recording
        </div>
      )}
      {stateKind === 'generating' && (
        <div className={styles.generatingBanner}>
          <span className={styles.spinner} />
          Generating with STS…
        </div>
      )}
    </div>
  );
}

// ── Left column: input device + reference-video volume, permission retry ──
export function BoothDeviceControls({
  permState,
  inputDevices,
  selectedDeviceId,
  onSelectDevice,
  onRetryPermission,
  busy,
  videoSrc,
  videoVolume,
  setVideoVolume,
}: {
  permState: PermState;
  inputDevices: AudioInputDevice[];
  selectedDeviceId: string;
  onSelectDevice(deviceId: string): void;
  onRetryPermission(): Promise<void>;
  busy: boolean;
  videoSrc: string | null;
  videoVolume: number;
  setVideoVolume: Dispatch<SetStateAction<number>>;
}) {
  return (
    <>
      {/* Combined controls row — Input Device picker + Reference-video
          volume + Headphones hint. Single horizontal row at desktop
          widths; wraps gracefully on narrow viewports (each cell has
          its own min-width and `flex-wrap` is on). The native <video
          controls> were removed (record/stop drives playback now) so
          the volume slider is the talent's only knob on the reference
          audio track. The volume slider is hidden when the active
          asset has no video; permission states are mutually exclusive. */}
      <div className={styles.controlsRow}>
        {permState.kind === 'pending' && (
          <div className={styles.deviceCell}>
            <span className={styles.deviceLabel}>
              <i className="ph-bold ph-microphone" />
              Input device
            </span>
            <div className={styles.devicePending}>
              <span className={styles.spinnerDark} />
              Requesting microphone access…
            </div>
          </div>
        )}
        {permState.kind === 'granted' && (
          <div className={styles.deviceCell}>
            <label htmlFor="sts-input-device" className={styles.deviceLabel}>
              <i className="ph-bold ph-microphone" />
              Input device
            </label>
            <select
              id="sts-input-device"
              className={styles.deviceSelect}
              value={selectedDeviceId}
              onChange={(e) => onSelectDevice(e.target.value)}
              disabled={busy}
            >
              <option value="">System default</option>
              {inputDevices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Volume slider styled to match the STS param sliders
            (uppercase muted label + decimal readout above a thin
            brandcolor-accent track). ~50% of the video width via
            flex-basis so the headphone hint can sit beside it. */}
        {videoSrc && (
          <div className={styles.volumeCell}>
            <div className={styles.volumeHeader}>
              <span className={styles.volumeLbl}>Video Audio</span>
              <span className={styles.volumeVal}>
                {Math.round(videoVolume * 100)}%
              </span>
            </div>
            <div className={styles.volumeSliderRow}>
              <button
                type="button"
                className={styles.videoVolumeIcon}
                onClick={() =>
                  setVideoVolume((v) => (v > 0 ? 0 : 0.25))
                }
                title={
                  videoVolume === 0
                    ? 'Unmute reference video'
                    : 'Mute reference video'
                }
                aria-label={
                  videoVolume === 0
                    ? 'Unmute reference video'
                    : 'Mute reference video'
                }
              >
                <i
                  className={
                    videoVolume === 0
                      ? 'ph-bold ph-speaker-slash'
                      : videoVolume < 0.5
                        ? 'ph-bold ph-speaker-low'
                        : 'ph-bold ph-speaker-high'
                  }
                />
              </button>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(videoVolume * 100)}
                onChange={(e) =>
                  setVideoVolume(Number(e.target.value) / 100)
                }
                className={styles.videoVolumeSlider}
                aria-label="Reference video volume"
              />
            </div>
          </div>
        )}

      </div>

      {permState.kind === 'denied' && (
        <div className={styles.permDenied}>
          <i className="ph-bold ph-microphone-slash" />
          <div className={styles.permDeniedBody}>
            <strong>Microphone access blocked.</strong>
            <span>
              Grant access in your browser to record. Most browsers show
              a mic icon in the address bar — allow it, then click
              Try again.
            </span>
          </div>
          <button
            type="button"
            className={styles.permDeniedRetry}
            onClick={() => void onRetryPermission()}
          >
            Try again
          </button>
        </div>
      )}
    </>
  );
}

// ── Middle column: voice picker, STS params, Generate ─────────────────────
export function BoothMidColumn({
  isMultiVoice,
  currentVoiceId,
  onVoiceChange,
  assetVoiceIds,
  voices,
  busy,
  params,
  onParamsChange,
  atLimit,
  sessionNum,
  takes,
  activeAssetVoiceId,
  hasSource,
  onGenerate,
}: {
  isMultiVoice: boolean;
  currentVoiceId: string;
  onVoiceChange(voiceId: string): void;
  assetVoiceIds: string[];
  voices: Voice[];
  busy: boolean;
  params: STSParams;
  onParamsChange(params: STSParams): void;
  atLimit: boolean;
  sessionNum: number | null;
  takes: DraftTake[];
  activeAssetVoiceId: string | undefined;
  /** A recorded / uploaded source is loaded and waiting for Generate. */
  hasSource: boolean;
  onGenerate(): Promise<void>;
}) {
  return (
    <div className={styles.colMid}>
      {/* Voice picker (multi-voice assets only, #173 Phase 6) — sits
          above the params card so the talent picks the target voice
          BEFORE adjusting any STS params. Single-voice assets bypass
          this entirely. */}
      {isMultiVoice && (
        <div className={styles.voicePickerRow}>
          <span className={styles.voicePickerLabel}>Voice</span>
          <div className={styles.voicePickerControl}>
            <VoicePicker
              value={currentVoiceId}
              onChange={onVoiceChange}
              assetVoiceIds={assetVoiceIds}
              voices={voices}
              disabled={busy}
            />
          </div>
        </div>
      )}
      <RecordingStudioParams
        params={params}
        onChange={onParamsChange}
        disabled={busy}
      />
      {atLimit && (
        <div className={styles.limitHint}>
          Session limit reached ({RECORDING_SESSION_TAKE_LIMIT} takes).
          Delete a take to record again.
        </div>
      )}

      {/* Generate button (#173 Phase 6) — sits below the params card so
          the talent's flow is: pick voice → tweak params → Generate.
          Label shifts to "ReGenerate Take s-n" when the same input +
          same voice already has a take (clear that clicking will
          overwrite that specific take). */}
      {(() => {
        const targetVoice = currentVoiceId || activeAssetVoiceId;
        const existing =
          sessionNum != null && targetVoice
            ? takes.find(
                (t) => t.session === sessionNum && t.voiceId === targetVoice,
              )
            : undefined;
        const willOverwrite = Boolean(existing && !existing.marked);
        const label = willOverwrite
          ? `ReGenerate Take ${existing!.session}-${existing!.takeInSession}`
          : 'Generate';
        return (
          <Button
            variant="primary"
            size="sm"
            disabled={!hasSource || busy}
            onClick={() => void onGenerate()}
            title={
              !hasSource
                ? 'Record or upload a source first'
                : willOverwrite
                  ? `Re-generate this voice's take with current params`
                  : isMultiVoice
                    ? 'Generate STS with selected voice'
                    : 'Generate STS'
            }
          >
            <i
              className={
                willOverwrite
                  ? 'ph-bold ph-arrow-clockwise'
                  : 'ph-fill ph-magic-wand'
              }
            />{' '}
            {label}
          </Button>
        );
      })()}
    </div>
  );
}

// ── Right column: takes list ──────────────────────────────────────────────
export function BoothTakesColumn({
  takes,
  playingTakeId,
  isMultiVoice,
  assetVoiceIds,
  setPlayingTakeId,
  onToggleMark,
  onDelete,
}: {
  takes: DraftTake[];
  playingTakeId: string | null;
  isMultiVoice: boolean;
  assetVoiceIds: string[];
  setPlayingTakeId: Dispatch<SetStateAction<string | null>>;
  onToggleMark(id: string): void;
  onDelete(id: string): void;
}) {
  return (
    <div className={styles.colRight}>
      <div className={styles.takesHeader}>
        <span>Takes</span>
        <span className={styles.takesBadge}>
          {takes.length} / {RECORDING_SESSION_TAKE_LIMIT}
        </span>
      </div>
      <div className={styles.takesList}>
        {takes.length === 0 ? (
          <div className={styles.takesEmpty}>
            <i className="ph-bold ph-waveform" />
            <span>
              Record or upload a source audio — takes will appear here.
            </span>
          </div>
        ) : (
          takes.map((t, i) => (
            <TakeCard
              key={t.id}
              label={formatTakeLabel(t, i)}
              take={t}
              playing={playingTakeId === t.id}
              shouldPause={playingTakeId !== null && playingTakeId !== t.id}
              voiceIndicator={
                isMultiVoice
                  ? {
                      color: voiceColorVar(t.voiceId, assetVoiceIds),
                      label: voiceLabel(t.voiceId, assetVoiceIds),
                    }
                  : null
              }
              onPlayStart={setPlayingTakeId}
              onPlayStop={() => setPlayingTakeId(null)}
              onToggleMark={onToggleMark}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ── Footer: hint + Cancel / Finish Recording ──────────────────────────────
export function BoothFooter({
  takes,
  hasUnmarked,
  busy,
  onCancel,
  onFinish,
}: {
  takes: DraftTake[];
  hasUnmarked: boolean;
  busy: boolean;
  onCancel(): void;
  onFinish(): void;
}) {
  return (
    <div className={styles.footer}>
      <div className={styles.footerHint}>
        {takes.length > 0 && hasUnmarked && (
          <span>
            <i className="ph-bold ph-info" />{' '}
            Only marked takes will be saved — unmarked takes will be removed.
          </span>
        )}
      </div>
      <div className={styles.footerActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onFinish}
          disabled={busy || takes.filter((t) => t.marked).length === 0}
          title={
            takes.filter((t) => t.marked).length === 0
              ? 'Mark at least one take with the check button'
              : undefined
          }
        >
          <i className="ph-fill ph-check" /> Finish Recording
        </Button>
      </div>
    </div>
  );
}
